import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  Building2, Plus, Search, Users, Eye, 
  Pause, Play, MoreVertical, Filter
} from 'lucide-react';
import { superAdminApi } from '../../utils/superAdminApi';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';

export const TenantsPage = () => {
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({
    company_name: '',
    contact_email: '',
    contact_phone: '',
    plan_id: '',
    billing_cycle: 'MONTHLY',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, plansRes] = await Promise.all([
        superAdminApi.getTenants({}),
        superAdminApi.getPlans()
      ]);
      setTenants(tenantsRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      const response = await superAdminApi.getTenants(params);
      setTenants(response.data);
    } catch (error) {
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      await superAdminApi.createTenant(newTenant);
      toast.success('Azienda creata con successo');
      setDialogOpen(false);
      setNewTenant({
        company_name: '',
        contact_email: '',
        contact_phone: '',
        plan_id: '',
        billing_cycle: 'MONTHLY',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleSuspend = async (tenantId) => {
    const ok = await confirm({
      title: 'Sospendi azienda',
      description: 'L\'azienda sarà sospesa e i suoi utenti non potranno più accedere finché non verrà riattivata.',
      confirmText: 'Sospendi',
      destructive: true,
    });
    if (!ok) return;
    try {
      await superAdminApi.suspendTenant(tenantId);
      toast.success('Azienda sospesa');
      loadData();
    } catch (error) {
      toast.error('Errore nella sospensione');
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await superAdminApi.activateTenant(tenantId);
      toast.success('Azienda attivata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'attivazione');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-emerald-500/20 text-emerald-400';
      case 'TRIAL': return 'bg-blue-500/20 text-blue-400';
      case 'SUSPENDED': return 'bg-red-500/20 text-red-400';
      case 'CANCELLED': return 'bg-slate-500/20 text-slate-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Attivo';
      case 'TRIAL': return 'Trial';
      case 'SUSPENDED': return 'Sospeso';
      case 'CANCELLED': return 'Cancellato';
      default: return status;
    }
  };

  if (loading && tenants.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Aziende</h1>
          <p className="text-slate-400">Gestisci le aziende registrate sulla piattaforma</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="create-tenant-btn">
              <Plus size={18} className="mr-2" />
              Nuova Azienda
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Crea Nuova Azienda</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Nome Azienda *</Label>
                <Input
                  value={newTenant.company_name}
                  onChange={(e) => setNewTenant({...newTenant, company_name: e.target.value})}
                  required
                  className="bg-slate-700 border-slate-600"
                  data-testid="tenant-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Contatto *</Label>
                  <Input
                    type="email"
                    value={newTenant.contact_email}
                    onChange={(e) => setNewTenant({...newTenant, contact_email: e.target.value})}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Telefono</Label>
                  <Input
                    value={newTenant.contact_phone}
                    onChange={(e) => setNewTenant({...newTenant, contact_phone: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Piano *</Label>
                  <Select 
                    value={newTenant.plan_id} 
                    onValueChange={(v) => setNewTenant({...newTenant, plan_id: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Seleziona piano" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {plans.map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.display_name} - CHF {plan.price_monthly_chf}/mese
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ciclo Fatturazione</Label>
                  <Select 
                    value={newTenant.billing_cycle} 
                    onValueChange={(v) => setNewTenant({...newTenant, billing_cycle: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="MONTHLY">Mensile</SelectItem>
                      <SelectItem value="YEARLY">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Note</Label>
                <Textarea
                  value={newTenant.notes}
                  onChange={(e) => setNewTenant({...newTenant, notes: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  Crea Azienda
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Cerca per nome o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600">
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="ACTIVE">Attivi</SelectItem>
                <SelectItem value="TRIAL">Trial</SelectItem>
                <SelectItem value="SUSPENDED">Sospesi</SelectItem>
                <SelectItem value="CANCELLED">Cancellati</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="bg-slate-700 hover:bg-slate-600">
              Cerca
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tenants List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50 border-b border-slate-700">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-300">Azienda</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-300">Piano</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-300">Utenti</th>
                  <th className="text-center p-4 text-sm font-semibold text-slate-300">Stato</th>
                  <th className="text-right p-4 text-sm font-semibold text-slate-300">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-400">
                      Nessuna azienda trovata
                    </td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-white">{tenant.company_name}</p>
                          <p className="text-sm text-slate-400">{tenant.contact_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300">{tenant.plan_name || '-'}</span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users size={16} className="text-slate-400" />
                          <span className="text-slate-300">
                            {tenant.user_count || 0}
                            {tenant.max_users > 0 && ` / ${tenant.max_users}`}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(tenant.status)}`}>
                          {getStatusLabel(tenant.status)}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/super-admin/tenants/${tenant.id}`}>
                            <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white">
                              <Eye size={16} />
                            </Button>
                          </Link>
                          {tenant.status === 'ACTIVE' ? (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleSuspend(tenant.id)}
                            >
                              <Pause size={16} />
                            </Button>
                          ) : tenant.status === 'SUSPENDED' && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-emerald-400 hover:text-emerald-300"
                              onClick={() => handleActivate(tenant.id)}
                            >
                              <Play size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
